// Plays the current line's clip, one at a time. Native: expo-av Audio.Sound (silent-switch override).
// Web: HTMLAudioElement, so the playing element can be captured as a MediaStreamTrack to drive SyQi's lip puppet.
// Missing clip / failed playback => nothing happens and the manual next-line button remains the fallback.
import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { Asset } from 'expo-asset';
import { Audio, type AVPlaybackStatus } from 'expo-av';
import { AUDIO_CLIPS } from './audioClips';

const WORD_MS_PER_CHAR = 60; // muted: reading delay = text length x 60ms

export function useLinePlayer({ lineId, text, enabled, muted, replayTick, onDone }: {
  lineId: string | undefined;
  text: string;
  /** Begin was tapped and onboarding is active. */
  enabled: boolean;
  muted: boolean;
  replayTick: number;
  /** Called when the clip finished (or the muted reading delay elapsed). */
  onDone: () => void;
}): { speaking: boolean; track: MediaStreamTrack | null } {
  const [speaking, setSpeaking] = useState(false);
  const [track, setTrack] = useState<MediaStreamTrack | null>(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!enabled || !lineId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stop: () => void = () => {};
    const finish = () => {
      if (cancelled) return;
      cancelled = true; // one completion per line
      setSpeaking(false);
      setTrack(null);
      doneRef.current();
    };

    if (muted) {
      timer = setTimeout(finish, Math.max(900, text.length * WORD_MS_PER_CHAR));
    } else {
      const clip = AUDIO_CLIPS[lineId];
      if (clip != null) {
        if (Platform.OS === 'web') {
          try {
            const el = new window.Audio(Asset.fromModule(clip).uri);
            stop = () => {
              el.onended = null;
              el.onerror = null;
              el.pause();
              el.removeAttribute('src');
              el.load();
            };
            el.onended = finish;
            el.onerror = () => setSpeaking(false); // failed: leave manual control
            void el
              .play()
              .then(() => {
                if (cancelled) return;
                setSpeaking(true);
                const cap = (el as HTMLAudioElement & { captureStream?: () => MediaStream }).captureStream;
                const t = cap ? cap.call(el).getAudioTracks()[0] : undefined;
                if (t) setTrack(t);
              })
              .catch(() => setSpeaking(false));
          } catch {
            /* manual fallback */
          }
        } else {
          let sound: Audio.Sound | null = null;
          stop = () => {
            const s = sound;
            sound = null;
            void s?.unloadAsync().catch(() => {});
          };
          void (async () => {
            try {
              await Audio.setAudioModeAsync({ playsInSilentModeIOS: true });
              const { sound: snd } = await Audio.Sound.createAsync(clip, { shouldPlay: true }, (st: AVPlaybackStatus) => {
                if (st.isLoaded && st.didJustFinish) finish();
              });
              if (cancelled) {
                void snd.unloadAsync().catch(() => {});
                return;
              }
              sound = snd;
              setSpeaking(true);
            } catch {
              /* manual fallback */
            }
          })();
        }
      }
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      stop();
      setSpeaking(false);
      setTrack(null);
    };
  }, [lineId, enabled, muted, replayTick, text]);

  return { speaking, track };
}
